/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Ronnel Bedana - ronnel@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import { Component, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { Report, Client, Helpers } from 'src/app/model';
import { ClientProvider, ReportProvider } from 'src/app/providers';
import { SearchableSelection } from 'src/app/model/interface/searchable.selection';
import { CreateNewClientModal } from 'src/app/components/modal';

@Component({
  selector: 'assign-collector-modal',
  templateUrl: './assign-collector.modal.html',
  styleUrls: ['./assign-collector.modal.scss']
})
export class AssignCollectorModal implements OnInit, OnDestroy {
  subject: Subject<boolean>;
  subscription: any;
  currentReport: Report;
  clients: Client[] = [];
  isAssignToBank = false;
  createModal: BsModalRef;
  selectedClient: Client;

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    public clientProvider: ClientProvider,
    private reportProvider: ReportProvider
  ) {}

  ngOnInit(): void {
    // Fix for when user clicks `ESC` instead of clicking, no Event was firing
    this.subscription = this.modalService.onHide.subscribe((reason: string) => {
      if (reason === 'esc')
        this.onCancel();
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
  }

  onConfirm(): void {
    this.bsModalRef.hide();
    this.subject.next(true);
    this.subject.complete();
  }

  onCancel(): void {
    this.bsModalRef.hide();
    this.subject.next(false);
    this.subject.complete();
  }

  assignToBank(): void {
    this.isAssignToBank = true;
  }

  createNewBank(): void {
    this.createModal = this.modalService.show(CreateNewClientModal, Helpers.getModalConfig());
    const ackSubject = new Subject<Client>();
    this.createModal.content.subject = ackSubject;
    this.createModal.content.isEdit = false;
    this.createModal.content.client.COMPANY_NAME = this.getBankName();
    this.createModal.content.client.DOMAINS = this.getDomains();

    ackSubject.subscribe(value => {
      if (value) {
        this.currentReport.CLIENT = Client.parse(value);
        this.reportProvider.assignToBank(this.currentReport.ID, this.currentReport.CLIENT.ID)
          .subscribe(data => {
            if (data.success) {
              this.onConfirm();
            }
          });
      }
    });
  }

  clientSelected(value: SearchableSelection): void {
    this.selectedClient = value ? value.VALUE : null;
  }

  assignClientToReport(): void {
    if (this.selectedClient) {
      this.reportProvider.assignToBank(this.currentReport.ID, this.selectedClient.ID)
        .subscribe(data => {
          if (data.success) {
            this.currentReport.CLIENT = Client.parse(this.selectedClient);
            this.onConfirm();
          }
        });
    }
  }

  getDomains(): string {
    const domains: string[] = [];

    for (const rcu of this.currentReport.CLIENT_USERS) {
      const email = rcu.CLIENT_USER.EMAIL;
      const domain = email.substr(email.indexOf('@') + 1);
      if (!domains.find(d => d === domain)) {
        domains.push(domain);
      }
    }

    return domains ? domains.join(', ') : null;
  }

  getBankName(): string {
    if (this.currentReport.CLIENT_USERS && this.currentReport.CLIENT_USERS.length > 0) {
      const rcu = this.currentReport.CLIENT_USERS[0];
      const email = rcu.CLIENT_USER.EMAIL;
      const domain = email.substr(email.indexOf('@') + 1).replace(/([.]\w+)$/, '');

      return domain.charAt(0).toUpperCase() + domain.slice(1);
    }

    return null;
  }

  onIgnore(): void {
    this.currentReport.ignore_collector = true;
    this.onCancel();
  }
}
